import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  Empty,
  Tabs,
  Table,
  Button,
  Tooltip,
  Badge,
  Modal,
  Segmented,
  AutoComplete,
  App
} from 'antd';
import {
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DownOutlined,
  UpOutlined,
  QuestionCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { logError, getErrorStats } from '../../utils/errorLogger';
import { isTauriAvailable } from '../../utils/tauriBridge';
import ErrorLogViewer from '../ErrorLogViewer';
import './index.css';
import { exportService, type ExportOptions } from '../../services/export';
import { analysisApi } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
// 后端 API 基址（开发环境默认 8001）
const getApiBaseUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_API_BASE_URL as string;
  if (!envUrl) return '/api';
  return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
};
const API_BASE_URL = getApiBaseUrl();
// 移除 TabPane 的解构，因为已弃用
// const { TabPane } = Tabs;

// 分析结果接口
interface AnalysisResult {
  symbol: string;
  company_name: string;
  timestamp: string;
  analysis_start_time?: string;
  analysis_end_time?: string;
  data_update_time?: string;
  data_source?: string;
  overall_signal: 'BUY' | 'SELL' | 'HOLD';
  display_action?: string; // 显示的建议动作（如“观望”）
  confidence: number;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
  expected_return: number;
  expected_return_detail?: {
    bullish_return: number;
    bearish_return: number;
    direction: string;
  };
  strategies: StrategyResult[];
  market_data: MarketData;
  technical_indicators: TechnicalIndicators;
  risk_metrics: RiskMetrics;
  analysis_duration?: number; // 分析耗时（毫秒）
  data_freshness?: 'FRESH' | 'STALE' | 'OUTDATED'; // 数据新鲜度
  // 组聚合结果（来自后端）
  group_contributions?: Record<string, {
    group_weight: number;
    group_score: number;
    group_confidence: number;
    group_risk: number;
  }>;
  // 投资摘要（来自后端）
  investment_summary?: {
    final_signal: { action: string; display: string };
    scores: { comprehensive: number; confidence_adjusted: number; confidence_original: number; risk: number };
    risk_assessment: { level: string; value: number; description: string };
    divergence_analysis: { divergence_std: number; polarization: number; penalty_factor: number };
    algorithm_participation?: {
        is_active: boolean;
        active_count: number;
        total_weight: number;
        algorithms: Array<{ name: string; type: string; weight: number; contribution: string }>;
    };
    advanced_risk_model?: {
        risk_penalty: number;
        overall_risk_level: string;
        warnings: string[];
        factors: any;
    };
    core_contradiction: string;
    key_metrics: { expected_return_5d: string; volatility: string };
    action_advice: { holder: string; buyer: string };
  };
}

interface StrategyResult {
  strategy_type: string;
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  weight: number;
  analysis_details: Record<string, any>;
}

  interface MarketData {
    current_price: number;
    high_price: number;
    low_price: number;
    price_change: number;
    price_change_percent: number;
    volume: number;
    market_cap: number;
    pe_ratio: number;
    pb_ratio: number;
  }

  // 工具函数：判断是否为场内ETF（159xxx.SZ 或 5xxxxx.SH）
  const isETFBySymbol = (symbol?: string): boolean => {
    try {
      const ts = String(symbol || '').toUpperCase();
      return /(^(159\d{3})\.(SZ)$)|(^(5\d{5})\.(SH)$)/.test(ts);
    } catch {
      return false;
    }
  };
  const normalizeSymbolInput = (raw?: string): string => {
    const s = String(raw || '').trim().toUpperCase();
    if (!s) return s;
    if (/^\d{6}$/.test(s)) {
      if (s.startsWith('5')) return `${s}.SH`;
      if (s.startsWith('1')) return `${s}.SZ`;
      return `${s}.${s.startsWith('6') ? 'SH' : 'SZ'}`;
    }
    return s;
  };

interface TechnicalIndicators {
  rsi: number;
  macd: {
    macd: number;
    signal: number;
    histogram: number;
  };
  bollinger_bands: {
    upper: number;
    middle: number;
    lower: number;
  };
  moving_averages: {
    ma5: number;
    ma10: number;
    ma20: number;
    ma50: number;
  };
}

interface RiskMetrics {
  volatility: number;
  max_drawdown: number;
  sharpe_ratio: number;
  var_95: number;
  cvar_95: number;
}

// 错误信息接口
interface ErrorInfo {
  type: 'NETWORK' | 'API' | 'DATA' | 'TIMEOUT' | 'UNKNOWN';
  code?: string | number;
  message: string;
  details?: string;
  suggestion?: string;
  timestamp: string;
  retryable: boolean;
}

interface AnalysisResultsProps {
  result: AnalysisResult | null;
  loading?: boolean;
  strategyLoading?: boolean;
  error?: string | ErrorInfo;
  onRefresh?: () => void;
  onPeriodChange?: (period: number) => void;
  // 选中的策略（用于右侧过滤展示）
  selectedStrategies?: string[];
  // 是否显示“数据不足”的占位策略
  showPlaceholders?: boolean;
  // K线样本数量（用于质量守卫与年化口径）
  klineSampleCount?: number;
  // 最新数据时间戳（用于数据新鲜度）
  latestTimestamp?: string | Date;
  // 近N日窗口（用于年化口径展示）
  windowDays?: number;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  result,
  loading = false,
  strategyLoading = false,
  error,
  onRefresh,
  onPeriodChange,
  selectedStrategies = [],
  showPlaceholders = false,
  // 诊断修复：解构并提供默认值，避免未定义引用
  klineSampleCount = 0,
  latestTimestamp = undefined,
  windowDays = undefined
}) => {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [errorLogVisible, setErrorLogVisible] = useState(false);
  const [riskModalVisible, setRiskModalVisible] = useState(false); // 风险详情弹窗
  const [investmentSummaryVisible, setInvestmentSummaryVisible] = useState(false); // 投资摘要弹窗
  const [healthStatus, setHealthStatus] = useState<{ backend: 'unknown' | 'ok' | 'fail'; mcp: 'unknown' | 'ok' | 'fail'; tauri: boolean }>({ backend: 'unknown', mcp: 'unknown', tauri: false });
  // 近N日预测收益周期（5/10/20）— Hooks需在组件顶层调用
  const [erPeriod, setErPeriod] = useState<number>(20);
  useEffect(() => {
    if (typeof onPeriodChange === 'function') onPeriodChange(erPeriod);
  }, []);
  const healthOnceRef = React.useRef(false);
  const [pairSymbol, setPairSymbol] = useState<string>('');
  const [pairOptions, setPairOptions] = useState<{ value: string; label: string }[]>([]);
  const [headerName, setHeaderName] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const symbol = (result?.symbol || '').trim();
        const name = (result?.company_name || '').trim();
        if (!symbol) return;
        if (name && name !== symbol) {
          setHeaderName(name);
          return;
        }
        const rt = await analysisApi.getRealtimeData(symbol);
        const nm = String(rt?.name || '').trim();
        if (nm) setHeaderName(nm);
      } catch { void 0; }
    })();
  }, [result?.symbol, result?.company_name]);

  // 健康检查：检测Tauri可用性、后端与MCP连通
  useEffect(() => {
    let mounted = true;
    (async () => {
      if ((window as any).__health_check_once__) return;
      (window as any).__health_check_once__ = true;
      const tauri = await isTauriAvailable();
      if (mounted) setHealthStatus((s) => ({ ...s, tauri }));
      try {
        // 移除 AbortController
        const req = fetch(`${API_BASE_URL}/stocks/health`);
        const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 1000));
        const resp = await Promise.race<[Response | null]>([req as any, timeout as any]) as any;
        
        if (!mounted) return;
        
        if (resp === null) {
          setHealthStatus((s) => ({ ...s, backend: 'fail', mcp: 'fail' }));
          return;
        }
        if (resp.ok) {
          const j = await resp.json().catch(() => ({}));
          const backendOk = String(j?.status || '').toLowerCase() === 'healthy';
          const mcpOk = !!j?.data_sources;
          setHealthStatus((s) => ({ ...s, backend: backendOk ? 'ok' : 'fail', mcp: mcpOk ? 'ok' : 'fail' }));
        } else {
          setHealthStatus((s) => ({ ...s, backend: 'fail', mcp: 'fail' }));
        }
      } catch (e: any) {
        if (mounted) {
          setHealthStatus((s) => ({ ...s, backend: 'fail', mcp: 'fail' }));
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 设置弹窗逻辑已移至图表数据区域，此处不再使用

  // 辅助函数：获取错误信息
  const getErrorInfo = (error: string | ErrorInfo): ErrorInfo => {
    if (typeof error === 'string') {
      const errorInfo: ErrorInfo = {
        code: 'UNKNOWN_ERROR',
        type: 'UNKNOWN',
        message: error,
        details: '未知错误，请联系技术支持',
        suggestion: '请刷新页面重试，如问题持续存在请联系技术支持',
        timestamp: new Date().toISOString(),
        retryable: true
      };
      
      // 记录错误日志
      logError({
        type: 'UNKNOWN',
        code: 'UNKNOWN_ERROR',
        message: error,
        details: '分析结果组件中的未知错误',
        suggestion: '请刷新页面重试，如问题持续存在请联系技术支持',
        retryable: true,
        severity: 'MEDIUM',
        component: 'AnalysisResults'
      });
      
      return errorInfo;
    }
    
    // 记录结构化错误日志
    logError({
      type: error.type as any,
      code: error.code,
      message: error.message,
      details: error.details,
      suggestion: error.suggestion,
      retryable: error.retryable,
      severity: error.type === 'TIMEOUT' ? 'HIGH' : 
                error.type === 'NETWORK' ? 'MEDIUM' : 
                error.type === 'API' ? 'HIGH' : 'LOW',
      component: 'AnalysisResults',
      context: {
        timestamp: error.timestamp,
        errorCode: error.code
      }
    });
    
    return error;
  };

  // 格式化时间显示
  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 计算数据新鲜度
  const getDataFreshness = (timestamp: string): { status: 'FRESH' | 'STALE' | 'OUTDATED', color: string, text: string } => {
    const now = new Date().getTime();
    const dataTime = new Date(timestamp).getTime();
    const diffMinutes = (now - dataTime) / (1000 * 60);

    if (diffMinutes <= 5) {
      return { status: 'FRESH', color: '#52c41a', text: '数据新鲜' };
    } else if (diffMinutes <= 30) {
      return { status: 'STALE', color: '#faad14', text: '数据较旧' };
    } else {
      return { status: 'OUTDATED', color: '#ff4d4f', text: '数据过期' };
    }
  };

  // 信号颜色映射
  const getSignalColor = (signal: string): string => {
    switch (signal) {
      case 'BUY': return '#52c41a';
      case 'SELL': return '#ff4d4f';
      case 'HOLD':
      case 'WAIT':
      case 'NEUTRAL': return '#faad14';
      default: return '#d9d9d9';
    }
  };

  // 风险等级颜色映射
  const getRiskColor = (risk: string): string => {
    switch (risk) {
      case 'LOW': return '#52c41a';
      case 'MEDIUM': return '#faad14';
      case 'HIGH': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  // ========================= 辅助格式化函数 =========================
  // 将大数值格式化为“亿/万”单位的短格式字符串（用于市值等金额类数据）
  // 说明：
  // - 当数值 >= 1e8 时显示为“X.Y亿”
  // - 当数值 >= 1e4 时显示为“X.Y万”
  // - 其他情况按原始数值显示
  // 注意：这里不再附加“元”后缀，避免出现“44.6亿 元”的重复单位问题
  const formatLargeNumberShort = (num: number): string => {
    const n = Number(num) || 0;
    if (n >= 100000000) return `${(n / 100000000).toFixed(1)}亿`;
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toString();
  };

  // 统一策略中文名称映射（14策略）
  const strategyNameMap: Record<string, string> = {
    'trend_following': '趋势跟踪',
    'mean_reversion': '均值回归',
    'statistical_arbitrage': '统计套利',
    'event_driven': '事件驱动',
    'grid_trading': '网格交易',
    'factor_investing': '因子投资',
    'volatility_trading': '波动率交易',
    'market_sentiment': '市场情绪',
    'ml_forecasting': '机器学习预测',
    'money_flow': '资金流向',
    'risk_parity': '风险平价',
    'sector_rotation': '板块轮动',
    'fundamental_quant': '基本面量化',
    'enhanced_event': '事件驱动增强',
    // 兼容后端可能返回的大写或带空格的键
    'statistical arbitrage': '统计套利'
  };

  const normalizeKey = (t: string) => String(t || '').toLowerCase().replace(/[\s-]+/g, '_');
  const toChineseSignal = (signal: string) => {
    const s = String(signal || '').toUpperCase();
    if (s === 'BUY') return '买入';
    if (s === 'SELL') return '卖出';
    if (s === 'HOLD' || s === 'WAIT' || s === 'NEUTRAL') return '观望';
    // 其他未知标记（如"SUMMARY"）统一显示“—”
    return '—';
  };

  const getSignalType = (signalField: any): string => {
    if (!signalField) return 'HOLD';
    if (typeof signalField === 'string') return signalField;
    if (typeof signalField === 'object') {
      const raw = signalField.signal_type || signalField.type || signalField.action;
      return typeof raw === 'string' ? raw : 'HOLD';
    }
    return 'HOLD';
  };

  // 策略表格列定义
  const strategyColumns = [
    {
      title: '策略类型',
      dataIndex: 'strategy_type',
      key: 'strategy_type',
      render: (type: string) => {
        const key = normalizeKey(type);
        return strategyNameMap[key] || type;
      }
    },
    {
      title: '信号',
      dataIndex: 'signal',
      key: 'signal',
      render: (_: any, record: any) => {
        const sig = getSignalType(record?.signal);
        
        // 健康度判定逻辑
        const weight = record.weight || 0;
        const confidence = record.confidence || 0;
        const riskLevel = record.risk_level;
        
        let healthColor = 'green';
        let healthStatus = '健康';
        
        if (weight === 0 || confidence < 0.2) {
          healthColor = 'red';
          healthStatus = '熔断';
        } else if (confidence < 0.5 || (riskLevel === 'high' && weight < 0.05)) {
          healthColor = 'orange';
          healthStatus = '降权';
        }

        return (
          <Space>
            <Tag color={getSignalColor(String(sig).toUpperCase())}>{toChineseSignal(sig)}</Tag>
            <Tooltip title={`健康度: ${healthStatus} (置信度:${(confidence*100).toFixed(0)}%, 权重:${(weight*100).toFixed(1)}%)`}>
              <div style={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                background: healthColor === 'red' ? '#ff4d4f' : healthColor === 'orange' ? '#faad14' : '#52c41a',
                boxShadow: '0 0 2px rgba(0,0,0,0.1)',
                cursor: 'help'
              }} />
            </Tooltip>
          </Space>
        );
      }
    },
    {
      title: '置信度',
      dataIndex: 'confidence',
      key: 'confidence',
      render: (confidence: number) => (
        <Progress
          percent={Math.round(confidence * 100)}
          size="small"
          strokeColor={confidence > 0.7 ? '#52c41a' : confidence > 0.4 ? '#faad14' : '#ff4d4f'}
        />
      )
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
      render: (weight: number) => `${(weight * 100).toFixed(1)}%`
    },
    {
      title: '说明',
      key: 'reason',
      onCell: () => ({ style: { whiteSpace: 'normal' } }),
      render: (_: any, record: any) => {
        // 优先使用 signal 中的 reason
        const signalReason = record?.signal?.reason;
        // 其次使用 analysis_details 中的 reason
                    const details = (record?.analysis_details || {}) as Record<string, any>;
        let reason = signalReason || details?.reason || details?.summary || '';

        // 如果是统计套利，且 reason 是 JSON 格式，尝试解析
        if (record.name === '统计套利' && typeof reason === 'string' && reason.startsWith('{')) {
            try {
                const parsed = JSON.parse(reason);
                if (parsed.reason) reason = parsed.reason;
            } catch { }
        }
        
        // 对于统计套利，如果 reason 是空的，尝试构造显示
        // 注意：record?.analysis_details 可能是 undefined，或者是一个对象
        // 在 JS 中，!reason 会匹配 null, undefined, "" 等
        if (record.name === '统计套利' && !reason) {
            // 确保 details 存在
            const safeDetails = details || {};
            const hasCorr = safeDetails.correlation !== undefined && safeDetails.correlation !== null;
            const hasZ = safeDetails.z_score !== undefined && safeDetails.z_score !== null;
            
            if (hasCorr && hasZ) {
                reason = `相关性: ${Number(safeDetails.correlation).toFixed(2)}, Z-Score: ${Number(safeDetails.z_score).toFixed(2)}`;
            } else if (safeDetails.message) {
                reason = safeDetails.message;
            }
        }

        // 截断过长的 reason
        if (reason && reason.length > 100) {
            reason = reason.substring(0, 100) + '...';
        }
        
        return reason ? (
          <Tooltip title={reason}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reason}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        );
      }
    }
  ];

  // 技术指标表格数据
  const technicalData = useMemo(() => {
    if (!result?.technical_indicators) return [];
    const { technical_indicators } = result as any;
    
    // 确保数据格式正确，处理可能的数据类型问题
    const safeValue = (value: any) => {
      if (typeof value === 'number' && !isNaN(value)) {
        return value.toFixed(2);
      }
      return '0.00';
    };
    
    return [
      {
        key: 'rsi',
        indicator: 'RSI',
        value: safeValue(technical_indicators.rsi),
        description: 'RSI > 70 超买，RSI < 30 超卖'
      },
      {
        key: 'macd',
        indicator: 'MACD',
        value: safeValue(technical_indicators.macd?.macd),
        description: 'MACD > 0 看涨，MACD < 0 看跌'
      },
      {
        key: 'ma5',
        indicator: 'MA5',
        value: safeValue(technical_indicators.moving_averages?.ma5),
        description: '5日移动平均线'
      },
      {
        key: 'ma20',
        indicator: 'MA20',
        value: safeValue(technical_indicators.moving_averages?.ma20),
        description: '20日移动平均线'
      }
    ];
  }, [result]);

  // 风险指标表格数据 - 移到早期返回之前
  const riskData = useMemo(() => {
    if (!result?.risk_metrics) return [];
    const { risk_metrics } = result as any;
    
    // 安全的数值格式化函数
    const safePercentage = (value: any) => {
      if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
        const v = Number(value);
        // 单位统一：
        // - 若 v 在 [0,1] 视为比例，×100
        // - 若 v > 1 视为已是百分值，直接显示
        const pct = v <= 1 ? v * 100 : v;
        return `${pct.toFixed(2)}%`;
      }
      return '0.00%';
    };
    
    const safeNumber = (value: any) => {
      if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
        return value.toFixed(3);
      }
      return '0.000';
    };
    
    return [
      {
        key: 'volatility',
        metric: '波动率',
        value: safePercentage(risk_metrics.volatility),
        description: '价格波动的标准差'
      },
      {
        key: 'max_drawdown',
        metric: '最大回撤',
        value: safePercentage(risk_metrics.max_drawdown),
        description: '历史最大亏损幅度'
      },
      {
        key: 'sharpe_ratio',
        metric: '夏普比率',
        value: safeNumber(risk_metrics.sharpe_ratio),
        description: '风险调整后收益率'
      },
      {
        key: 'var_95',
        metric: 'VaR (95%)',
        value: safePercentage(risk_metrics.var_95),
        description: '95%置信度下的风险价值'
      },
      {
        key: 'cvar_95',
        metric: 'CVaR (95%)',
        value: safePercentage(risk_metrics.cvar_95),
        description: '95%置信度下的条件风险价值'
      }
    ];
  }, [result]);

  // 创建 Tabs items 配置 - 移到早期返回之前
  const getFreshness = () => {
    try {
      const ts = latestTimestamp ? new Date(latestTimestamp as any).getTime() : 0;
      if (!ts) return { label: '未知', color: 'default' };
      const now = Date.now();
      const diffDays = (now - ts) / (1000 * 60 * 60 * 24);
      if (diffDays <= 2) return { label: 'FRESH', color: 'green' };
      if (diffDays <= 7) return { label: 'STALE', color: 'orange' };
      return { label: 'OUTDATED', color: 'red' };
    } catch {
      return { label: '未知', color: 'default' };
    }
  };

  const tabItems = useMemo(() => {
    if (!result) return [];
    
    return [
      {
        key: 'overview',
        label: '概览',
        children: (
          <Row gutter={[16, 16]}>
            {/* 核心指标 */}
            <Col span={24}>
              <Row gutter={[16, 16]}>
                {/* 综合信号 */}
                <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                  <Card 
                    size="small" 
                    className="metric-card" 
                    style={{ minHeight: '120px', cursor: 'pointer' }}
                    onClick={() => setInvestmentSummaryVisible(true)}
                  >
                    {(() => {
                      const displayAction = (result as any).display_action;
                      const overallSignal = (result as any).overall_signal;
                      
                      console.log('🔍 综合信号 - 显示数据:', {
                        overall_signal: overallSignal,
                        display_action: displayAction,
                        confidence: (result as any).confidence,
                        risk_level: (result as any).risk_level,
                        expected_return: (result as any).expected_return
                      });
                      
                      // 优先使用后端返回的 display_action，否则使用 overall_signal 映射
                      const displayText = displayAction || (
                        overallSignal === 'BUY' ? '买入' : 
                        overallSignal === 'SELL' ? '卖出' : '观望'
                      );

                      return (
                        <>
                          <Statistic
                            title="综合信号"
                            value={displayText}
                            valueStyle={{ 
                              color: getSignalColor(overallSignal),
                              fontSize: '18px',
                              fontWeight: 'bold'
                            }}
                            prefix={
                              overallSignal === 'BUY' ? <RiseOutlined /> :
                              overallSignal === 'SELL' ? <FallOutlined /> :
                              <BarChartOutlined />
                            }
                          />
                          <div style={{ marginTop: 6, textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>点击查看投资摘要详情 <InfoCircleOutlined /></Text>
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                </Col>
                
                {/* 置信度 */}
                <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                  <Card size="small" className="metric-card" style={{ minHeight: '120px' }}>
                    {(() => {
                      const confRaw = (result as any)?.confidence;
                      const conf = (typeof confRaw === 'number' && isFinite(confRaw)) ? confRaw : 0.5;
                      const confSrc = (typeof window !== 'undefined' && localStorage.getItem('FINAL_CONF_FROM_GROUPS') === '1') ? '组' : '策略';
                      return (
                        <>
                          <Statistic
                            title={<span>置信度</span>}
                            value={Math.round(conf * 100)}
                            suffix="%"
                            valueStyle={{ 
                              color: conf > 0.7 ? '#52c41a' : conf > 0.4 ? '#faad14' : '#ff4d4f',
                              fontSize: '18px',
                              fontWeight: 'bold'
                            }}
                          />
                          <Progress
                            percent={Math.round(conf * 100)}
                            size="small"
                            strokeColor={conf > 0.7 ? '#52c41a' : conf > 0.4 ? '#faad14' : '#ff4d4f'}
                            showInfo={false}
                            style={{ marginTop: '8px' }}
                          />
                          <div style={{ marginTop: 6 }}>
                            <Text type="secondary">口径: {confSrc}</Text>
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                </Col>
                
                {/* 风险等级 */}
                <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                  <Card 
                    size="small" 
                    className="metric-card" 
                    style={{ minHeight: '120px', cursor: 'pointer' }}
                    onClick={() => setRiskModalVisible(true)}
                  >
                    {(klineSampleCount || 0) < 30 && (
                      <Alert type="warning" showIcon message="数据样本不足，风险与收益仅供参考" style={{ marginBottom: 8 }} />
                    )}
                    <Statistic
                      title={<span style={{ fontWeight: 600 }}>风险等级</span>}
                      value={result.risk_level === 'LOW' ? '低' :
                             result.risk_level === 'MEDIUM' ? '中' : '高'}
                      valueStyle={{ 
                        color: getRiskColor(result.risk_level),
                        fontSize: '18px',
                        fontWeight: 'bold'
                      }}
                    />
                    <Tag 
                      color={getRiskColor(result.risk_level)}
                      style={{ marginTop: '8px' }}
                    >
                      {result.risk_level}
                    </Tag>
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary">口径: {(typeof window !== 'undefined' && localStorage.getItem('FINAL_RISK_FROM_GROUPS') === '1') ? '组' : '策略'}</Text>
                      <Tooltip title="风险等级按国际惯例：低(Low)/中(Medium)/高(High)。点击查看 VaR(95%)、CVaR(95%) 与年化波动率详情。" style={{ marginLeft: 6 }}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                  </Card>
                </Col>
                
              </Row>
            </Col>

            {/* 近N日预测收益：单卡大面板 */}
            <Col span={24}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Card size="small" className="metric-card" style={{ minHeight: '160px' }}>
                    {(() => {
                      // 1. 尝试从 periods 对象获取
                      const weightedPeriods = (result as any)?.expected_returns_periods_weighted || {};
                      const unweightedPeriods = (result as any)?.expected_returns_periods || {};
                      const periods = Object.keys(weightedPeriods).length ? weightedPeriods : unweightedPeriods;
                      
                      // 2. 尝试从 investment_summary.key_metrics 获取准确值 (字符串如 "2.02%")
                      const summaryMetric = (result as any)?.investment_summary?.key_metrics?.expected_return_5d;
                      let valueFromSummary = null;
                      if (summaryMetric && typeof summaryMetric === 'string') {
                         valueFromSummary = parseFloat(summaryMetric.replace('%', ''));
                      }

                      // 3. 计算最终显示值
                      let valueFromApi = 0;
                      let isAlphaDecay = false; // 标记是否使用了 Alpha Decay
                      
                      // 步骤 A: 获取最可靠的 5日基准值 (百分比数值)
                      let base5dVal: number | null = null;
                      if (valueFromSummary !== null && !isNaN(valueFromSummary)) {
                          base5dVal = valueFromSummary;
                      } else {
                          const p5 = periods[5] ?? periods['5'];
                          if (typeof p5 === 'number') {
                              base5dVal = p5;
                          } else {
                              const erRaw = (result as any)?.expected_return_weighted ?? (result as any)?.expected_return;
                              if (typeof erRaw === 'number' && isFinite(erRaw)) {
                                  base5dVal = erRaw;
                              }
                          }
                      }

                      // 步骤 B: 根据周期计算
                      if (erPeriod === 5) {
                          // 5日直接显示基准值
                          valueFromApi = base5dVal ?? 0;
                      } else {
                          // 10日/20日：强制使用前端 Alpha Decay 模型，忽略后端可能存在的错误数据(如15%/30%)
                          if (base5dVal !== null) {
                              // Alpha Decay: Ret_T = Ret_5 * (T/5)^0.8
                              valueFromApi = base5dVal * Math.pow(erPeriod / 5.0, 0.8);
                              
                              // 再次检查异常值：如果基准值很小(<5%)但计算结果很大(>20%)，进行次级修正
                              if (Math.abs(base5dVal) < 5 && Math.abs(valueFromApi) > 20) {
                                  valueFromApi = base5dVal * Math.pow(erPeriod / 5.0, 0.6);
                              }
                              isAlphaDecay = true;
                          } else {
                              // 实在没有基准值，回退到 periods 读取（虽然可能不准）
                              const periodVal = periods[erPeriod] ?? periods[String(erPeriod)];
                              if (typeof periodVal === 'number') {
                                  valueFromApi = periodVal;
                              }
                          }
                      }
                      
                      const isWeighted = Object.keys(weightedPeriods).length > 0 || (typeof (result as any)?.expected_return_weighted === 'number');
                      // 如果使用了 Alpha Decay，强制显示 Alpha 标签；否则按原逻辑
                      // 5日标签更新为 "策略加权(Base)" 以匹配 "衰减估算(Alpha)" 的风格
                      const tagLabel = (erPeriod !== 5 && isAlphaDecay) ? '衰减估算(Alpha)' : (isWeighted ? '策略加权(Base)' : 'EWMA回退');
                      const tagColor = (erPeriod !== 5 && isAlphaDecay) ? 'geekblue' : (isWeighted ? 'green' : 'geekblue');
                      
                      // 获取置信区间和风险折扣数据
                      const intervals = (result as any)?.prediction_intervals || {};
                      const currentInterval = intervals[String(erPeriod)] || intervals[erPeriod];
                      const riskDiscount = (result as any)?.investment_summary?.risk_discount;

                      const nLabel = `近${erPeriod}日预测收益`;
                      return (
                        <>
                          <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Space>
                              <span style={{ whiteSpace: 'nowrap' }}>{nLabel}</span>
                              <Tooltip title="基于策略加权与历史收益估计；观望时显示约0%；数值仅供参考">
                                <InfoCircleOutlined />
                              </Tooltip>
                            </Space>
                            <Segmented size="small" options={[5, 10, 20]} value={erPeriod} onChange={(v) => { const n = Number(v); setErPeriod(n); if (typeof onPeriodChange === 'function') onPeriodChange(n); }} />
                          </Space>
                          <Row>
                            <Col span={24}>
                              <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
                                <Tag color={tagColor}>{`口径:${tagLabel}`}</Tag>
                                <Statistic value={valueFromApi} suffix="%" precision={2} valueStyle={{ color: valueFromApi > 0 ? '#52c41a' : '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }} prefix={valueFromApi > 0 ? <RiseOutlined /> : <FallOutlined />} />
                                
                                {/* 风险折扣提示 */}
                                {riskDiscount?.applied && (
                                    <Tooltip title={`因高风险触发前瞻性折扣 (因子: ${riskDiscount.factor?.toFixed(2)})`}>
                                        <div style={{ display: 'flex', alignItems: 'center', color: '#faad14', cursor: 'pointer', fontSize: '12px', background: '#fffbe6', padding: '2px 6px', borderRadius: '4px', border: '1px solid #ffe58f' }}>
                                            <WarningOutlined style={{ marginRight: 4 }} />
                                            <span>已折扣</span>
                                        </div>
                                    </Tooltip>
                                )}
                              </div>
                            </Col>
                            
                            {/* 置信区间展示 */}
                            {currentInterval && (
                                <Col span={24} style={{ marginTop: 2, marginBottom: 4 }}>
                                    <div style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
                                        <span style={{ marginRight: 4 }}>置信区间(95%):</span>
                                        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                            {Number(currentInterval.lower)?.toFixed(2)}% ~ {Number(currentInterval.upper)?.toFixed(2)}%
                                        </span>
                                    </div>
                                </Col>
                            )}
                            {/* 前端估算的置信区间展示 (当后端未返回时兜底) */}
                            {!currentInterval && erPeriod !== 5 && isAlphaDecay && (
                                <Col span={24} style={{ marginTop: 2, marginBottom: 4 }}>
                                    <div style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
                                        <span style={{ marginRight: 4 }}>置信区间(95%):</span>
                                        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                            {/* 简单估算：基于 Alpha 衰减值的 ±20% 范围 */}
                                            {(valueFromApi * 0.8).toFixed(2)}% ~ {(valueFromApi * 1.2).toFixed(2)}%
                                        </span>
                                        <span style={{ fontSize: '10px', color: '#999', marginLeft: 4 }}>(估)</span>
                                    </div>
                                </Col>
                            )}

                            {/* 多空收益详情 */}
                            {result.expected_return_detail && (
                               <Col span={24} style={{ marginTop: 4 }}>
                                 <Space size="small" style={{ fontSize: '12px' }}>
                                    <Text type="success">看多预期: +{(result.expected_return_detail.bullish_return).toFixed(2)}%</Text>
                                    <Text type="secondary">|</Text>
                                    <Text type="danger">看空预期: -{(result.expected_return_detail.bearish_return).toFixed(2)}%</Text>
                                 </Space>
                               </Col>
                            )}
                            <Col span={24} style={{ marginTop: 8 }}>
                              <Text type="secondary">说明：该值为近{erPeriod}日的预期收益百分比，基于策略加权与市场状态衰减模型估算；仅供参考。</Text>
                            </Col>
                          </Row>
                        </>
                      );
                    })()}
                  </Card>
                </Col>
              </Row>
            </Col>

            {/* 市场数据 */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <span>市场数据</span>
                    <Tooltip title={
                      '单位与口径说明：当前价格单位“元”；市值单位“亿元”；PE为TTM（滚动市盈率）；PB为市净率。估值可能受交易日与数据源限流影响。'
                    }>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                } 
                size="small"
              >
                <Row gutter={[12, 12]}>
                  {/* 第一行：当前价格和涨跌幅 */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Statistic
                      title="当前价格"
                      value={result.market_data?.current_price || 0}
                      precision={2}
                      suffix="元"
                      valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Statistic
                      title="涨跌额"
                      value={result.market_data?.price_change || 0}
                      precision={2}
                      suffix="元"
                      valueStyle={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold',
                        color: (result.market_data?.price_change || 0) >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                      prefix={(result.market_data?.price_change || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Statistic
                      title="涨跌幅"
                      value={result.market_data?.price_change_percent || 0}
                      precision={2}
                      suffix="%"
                      valueStyle={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold',
                        color: (result.market_data?.price_change_percent || 0) >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                      prefix={(result.market_data?.price_change_percent || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Statistic
                      title="成交量"
                      value={result.market_data?.volume || 0}
                      formatter={(value) => {
                        const num = Number(value);
                        if (num >= 100000000) return `${(num / 100000000).toFixed(1)}亿`;
                        if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
                        return num.toString();
                      }}
                      suffix="手"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  
                  {/* 第二行：价格区间 */}
                  <Col xs={12} sm={12} md={8} lg={6}>
                    <Statistic
                      title="当日最高"
                      value={result.market_data?.high_price || 0}
                      precision={2}
                      suffix="元"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6}>
                    <Statistic
                      title="当日最低"
                      value={result.market_data?.low_price || 0}
                      precision={2}
                      suffix="元"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Statistic
                      // ETF显示“基金规模(估算)”，股票显示“市值(亿元)”
                      title={isETFBySymbol(result.symbol) ? '基金规模(估算)' : '市值(亿元)'}
                      value={result.market_data?.market_cap ?? 0}
                      precision={1}
                      suffix={isETFBySymbol(result.symbol) ? '亿' : ''}
                      valueStyle={{ fontSize: '14px' }}
                      formatter={(value: any) => {
                        let num = Number(value);
                        if (!isFinite(num) || num === 0) return '—';
                        // 兼容后端未换算到“亿元”的情况：若值看起来是“元”（>1e9），则转为“亿元”
                        if (!isETFBySymbol(result.symbol) && num > 1e9) {
                          num = num / 1e8;
                        }
                        return num.toFixed(1);
                      }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Space align="center">
                          <Text type="secondary" style={{ fontSize: '12px' }}>PE (TTM)</Text>
                          {/* 市盈率口径标签：ttm/static/forward/unknown */}
                          { (result as any)?.market_data?.pe_ratio_kind && (
                            <Tag color="blue" style={{ marginLeft: 6 }}>
                              {((result as any).market_data.pe_ratio_kind || '').toUpperCase()}
                            </Tag>
                          )}
                        </Space>
                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          {(() => {
                            const v = result.market_data?.pe_ratio;
                            if (v === null || v === undefined) return '—';
                            const num = Number(v);
                            return isFinite(num) && num !== 0 ? num.toFixed(2) : '—';
                          })()}
                        </div>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>市净率</Text>
                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          {(() => {
                            const v = result.market_data?.pb_ratio;
                            if (v === null || v === undefined) return '—';
                            const num = Number(v);
                            return isFinite(num) && num !== 0 ? num.toFixed(2) : '—';
                          })()}
                        </div>
                      </div>
                    </Space>
                  </Col>
                  {/* 估值缺失提示：当市值/PE/PB均缺失时显示文案 */}
                  {(() => {
                    const mc = result.market_data?.market_cap;
                    const pe = result.market_data?.pe_ratio;
                    const pb = result.market_data?.pb_ratio;
                    const missing = (mc == null || mc === 0) && (pe == null || pe === 0) && (pb == null || pb === 0);
                    return missing ? (
                      <Col span={24}>
                        <Alert type="warning" showIcon message="暂无估值（数据源未返回或限流）" style={{ marginTop: 8 }} />
                      </Col>
                    ) : null;
                  })()}
                </Row>
              </Card>
            </Col>

            {/* 综合评判说明（标签+进度条+详情折叠） */}
            <Col span={24}>
              <Card size="small" title={
                <Space>
                  <span>综合评判说明</span>
                  <Tooltip title="基于各策略的权重×置信度×信号强度的加权结果，列出主要贡献来源">
                    <InfoCircleOutlined />
                  </Tooltip>
                  {strategyLoading && <Tag color="processing" icon={<Spin />}>AI思考中...</Tag>}
                </Space>
              }>
                {(() => {
                  if (strategyLoading && (!result.strategies || result.strategies.length === 0)) {
                     return (
                       <div style={{ textAlign: 'center', padding: '20px' }}>
                         <Spin tip="AI正在深入分析各策略信号..." />
                       </div>
                     );
                  }

                  console.log('🔍 综合评判说明 - 原始策略数据:', result.strategies);
                  const allStrategies = result.strategies || [];
                  console.log('🔍 综合评判说明 - 策略数量:', allStrategies.length);
                  
                  const items = allStrategies
                    .map(s => {
                      const item = {
                        type: s.strategy_type,
                        weight: s.weight || 0,
                        confidence: s.confidence || 0,
                        signal: s.signal,
                        score: (s.weight || 0) * (s.confidence || 0),
                        strength: (s as any)?.analysis_details?.strength ?? 0,
                        holding: (s as any)?.holding_period ?? undefined,
                        reason: (s as any)?.reason || (s as any)?.analysis_details?.reason || ''
                      };
                      console.log(`🔍 综合评判说明 - 策略评分: ${s.strategy_type}`, item);
                      return item;
                    })
                    .sort((a, b) => b.score - a.score)
                    .slice(0, 3);
                    
                  console.log('🔍 综合评判说明 - 最终显示项目:', items);
                  console.log('🔍 综合评判说明 - 处理后策略数据:', items);
                  if (items.length === 0) {
                    return <Text type="secondary">暂无策略贡献信息</Text>;
                  }
                  return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {items.map((it, idx) => {
                        const signalColor = it.signal === 'BUY' ? '#52c41a' : it.signal === 'SELL' ? '#ff4d4f' : '#d3d3d3';
                        return (
                          <div key={idx} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                            {/* 第一行：策略名 + 标签 */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Space>
                                <Text strong style={{ fontSize: 14 }}>{strategyNameMap[normalizeKey(it.type)] || it.type}</Text>
                                <Tag color={signalColor} style={{ margin: 0 }}>{it.signal}</Tag>
                              </Space>
                            </div>
                            {/* 第二行：贡献分 | 权重 | 置信 | 强度  */}
                            <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#595959' }}>
                              <div>贡献分 <b>{(it.score * 100).toFixed(1)}</b></div>
                              <div>权重 <b>{(it.weight * 100).toFixed(1)}%</b></div>
                              <div>置信 <b>{(it.confidence * 100).toFixed(0)}%</b></div>
                              <div>强度 <b>{Number(it.strength).toFixed(2)}</b></div>
                              <div>持有期 <b>{it.holding ? `${it.holding}d` : (() => {
                                const s = Number(it.strength || 0);
                                if (s >= 0.7) return '20-60d';
                                if (s >= 0.4) return '10-20d';
                                return '5-10d';
                              })()}</b></div>
                            </div>
                            {/* 进度条：用强度做 0-1 进度 */}
                            <div style={{ marginTop: 6 }}>
                              <div style={{ background: '#f0f0f0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(100, it.strength * 100)}%`, height: 6, background: signalColor, borderRadius: 4 }} />
                              </div>
                            </div>
                            {/* 详情：原因（简版，避免嵌套滚动） */}
                            {it.reason && (
                              <Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0, fontSize: 12 }}>
                                {it.reason.length > 120 ? it.reason.slice(0, 120) + '…' : it.reason}
                              </Paragraph>
                            )}
                          </div>
                        );
                      })}
                      <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
                        说明：综合信号由各策略的权重、置信度及信号共同决定。权重越高、置信度越强的策略对最终结论影响更大；风险等级由波动/回撤等指标综合评估。
                      </Paragraph>
                    </Space>
                  );
                })()}
              </Card>
            </Col>
          </Row>
        )
      },
      {
        key: 'strategies',
        label: (
          <Space>
             策略分析
             {strategyLoading && <Spin size="small" />}
          </Space>
        ),
        children: (
          <Table
            dataSource={(() => {
              const list = (showPlaceholders ? ((result as any).strategies_all || result.strategies || []) : (result.strategies || [])) as any[];
              
              // 🔍 调试日志：原始数据
              console.log('🔍 策略分析 - 原始数据:', {
                showPlaceholders,
                totalStrategies: list.length,
                sampleData: list.slice(0, 3),
                hasStrategiesAll: !!(result as any).strategies_all,
                hasStrategies: !!result.strategies
              });
              // 开启“显示占位”时：不受“所选策略”过滤，直接展示（含未启用/分析失败/数据不足）
              if (showPlaceholders) {
                console.log('🔍 策略分析 - 显示占位模式，返回所有策略:', list.length);
                return list.map((strategy, index) => ({ ...strategy, key: index }));
              }
              // 关闭“显示占位”时：受“所选策略”过滤，且隐藏占位项
              const normalizedSelected = (selectedStrategies || []).map(s => s.toLowerCase());
              console.log('🔍 策略分析 - 选中策略过滤:', normalizedSelected);
              const filteredBySwitch = normalizedSelected.length > 0 ? list.filter(s => {
                const strategyType = normalizeKey(s.strategy_type);
                const isIncluded = normalizedSelected.includes(strategyType);
                console.log(`🔍 策略分析 - 策略类型过滤: ${strategyType} -> ${isIncluded}`);
                return isIncluded;
              }) : list;
              
              console.log('🔍 策略分析 - 策略类型过滤后:', filteredBySwitch.length);
              const finalList = filteredBySwitch.filter(s => {
                const reason = (s?.analysis_details?.reason || '').toString();
                const isPlaceholder = !!(s?.analysis_details?.is_placeholder);
                const strategyName = s.strategy_type || 'unknown';
                
                console.log(`🔍 策略分析 - 过滤检查: ${strategyName}`, {
                  reason,
                  isPlaceholder,
                  hasAnalysisDetails: !!s?.analysis_details,
                  signal: s.signal,
                  confidence: s.confidence
                });
                
                if (isPlaceholder) {
                  console.log(`🔍 策略分析 - 过滤掉占位策略: ${strategyName}`);
                  return false;
                }
                
                // 只有当reason完全匹配这些关键词时才过滤，避免误杀包含这些词的有效分析
                if (reason === '未启用') {
                  console.log(`🔍 策略分析 - 过滤掉未启用: ${strategyName}`);
                  return false;
                }
                if (reason === '分析失败') {
                  console.log(`🔍 策略分析 - 过滤掉分析失败: ${strategyName}`);
                  return false;
                }
                if (reason === '数据不足') {
                  console.log(`🔍 策略分析 - 过滤掉数据不足: ${strategyName}`);
                  return false;
                }
                
                console.log(`🔍 策略分析 - 保留策略: ${strategyName}`);
                return true;
              });
              
              console.log('🔍 策略分析 - 最终显示策略数量:', finalList.length);
              console.log('🔍 策略分析 - 最终策略列表:', finalList.map(s => ({
                name: s.strategy_type,
                signal: s.signal,
                confidence: s.confidence,
                reason: s?.analysis_details?.reason
              })));
              return finalList.map((strategy, index) => ({ ...strategy, key: index }));
            })()}
            columns={strategyColumns}
            pagination={false}
            size="small"
          />
        )
      },
      {
        key: 'groups',
        label: '组维度',
        children: (() => {
          const gc = ((result as any).group_contributions || {}) as Record<string, any>;
          const groupName: Record<string, string> = {
            directional: '方向性策略',
            market_neutral: '市场中性策略',
            asset_allocation: '资产配置策略',
            event_driven: '事件驱动策略'
          };
          const data = Object.keys(gc).map((k, idx) => ({
            key: idx,
            group: groupName[k] || k,
            weight: Number(gc[k]?.group_weight || 0),
            score: Number(gc[k]?.group_score || 0),
            confidence: Number(gc[k]?.group_confidence || 0),
            risk: Number(gc[k]?.group_risk || 0)
          }));
          const order = ['directional','market_neutral','asset_allocation','event_driven'];
          const colors: Record<string, string> = {
            directional: '#1890ff',
            market_neutral: '#13c2c2',
            asset_allocation: '#faad14',
            event_driven: '#eb2f96'
          };
          // 组权重堆叠条
          const totalWeight = order.reduce((s,k)=> s + (Number(gc[k]?.group_weight||0)), 0) || 1;
          const weightSegments = order.map(k => ({ k, pct: ((Number(gc[k]?.group_weight||0)/totalWeight)*100) }));
          return (
            <>
              {/* 组权重环形图 */}
              <Card size="small" style={{ marginBottom: 12 }} title="组权重分布">
                {(() => {
                  const pct = order.map(k => (Number(gc[k]?.group_weight||0)/totalWeight)*100);
                  const stops = [`${colors[order[0]]} 0% ${pct[0]}%`, `${colors[order[1]]} ${pct[0]}% ${pct[0]+pct[1]}%`, `${colors[order[2]]} ${pct[0]+pct[1]}% ${pct[0]+pct[1]+pct[2]}%`, `${colors[order[3]]} ${pct[0]+pct[1]+pct[2]}% 100%`].join(', ');
                  const legendItemStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', borderRadius: 8, color: '#fff', fontSize: 11 };
                  const legendTextStyle: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word' };
                  const legendValueStyle: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
                  // 计算每段中心角度，用于在圆环上放置百分比标签
                  const centers: number[] = (() => {
                    let acc = 0;
                    return pct.map(p => { const mid = acc + p/2; acc += p; return mid * 3.6; });
                  })();
                  const labelStyle = (deg: number, color: string): React.CSSProperties => ({
                    position: 'absolute', left: '50%', top: '50%',
                    transform: `translate(-50%,-50%) rotate(${deg}deg) translate(46px) rotate(${-deg}deg)`,
                    fontSize: 10, color: '#333', background: 'rgba(255,255,255,0.85)',
                    padding: '0 4px', borderRadius: 4, border: `1px solid ${color}20`,
                    whiteSpace: 'nowrap'
                  });
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 150px', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 92, height: 92, borderRadius: '50%', backgroundImage: `conic-gradient(${stops})`, position: 'relative', margin: '0 auto' }}>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 66, height: 66, background: '#fff', borderRadius: '50%' }} />
                        {centers.map((deg, idx) => (
                          <div key={order[idx]} style={labelStyle(deg, colors[order[idx]])}>{pct[idx].toFixed(1)}%</div>
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                        {order.map((k, idx) => (
                          <div key={k} style={{ ...legendItemStyle, background: colors[k] }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#fff', opacity: 0.8 }} />
                              <span style={legendTextStyle}>{groupName[k]}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </Card>
              {/* 组分数条形图（简版） */}
              <Card size="small" style={{ marginBottom: 12 }} title="组分数">
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10 }}>
                  {order.map(k => (
                    <React.Fragment key={k}>
                      <div style={{ textAlign: 'right', paddingRight: 8, color: '#595959' }}>{groupName[k]}</div>
                      <div style={{ background: '#f5f5f5', borderRadius: 6 }}>
                        <div style={{ width: `${Math.min(100, Math.abs(Number(gc[k]?.group_score||0))*100)}%`, height: 12, background: colors[k], borderRadius: 6 }} />
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </Card>
              <Table
                dataSource={data}
                pagination={false}
                size="small"
                columns={[
                  { title: '组别', dataIndex: 'group', key: 'group' },
                  { title: '组权重', dataIndex: 'weight', key: 'weight', render: (v: number) => `${(v * 100).toFixed(1)}%` },
                  { title: '组分数', dataIndex: 'score', key: 'score', render: (v: number) => v.toFixed(3) },
                  { title: '组置信', dataIndex: 'confidence', key: 'confidence', render: (v: number) => `${(v * 100).toFixed(0)}%` },
                  { title: '组风险', dataIndex: 'risk', key: 'risk', render: (v: number) => `${(v * 100).toFixed(0)}%` },
                  { title: '复合风险', dataIndex: 'risk_composite', key: 'risk_composite', render: (_: any, row: any) => {
                    const key = order.find(k => (groupName[k] === row.group) || (k === row.group)) || '';
                    const rc = Number(gc[key]?.group_risk_composite ?? gc[key]?.group_risk ?? 0);
                    return `${(rc*100).toFixed(0)}%`;
                  } }
                ]}
              />
            </>
          );
        })()
      },
      {
        key: 'technical',
        label: '技术指标',
        children: (
          <Table
            dataSource={technicalData}
            columns={[
              {
                title: '指标',
                dataIndex: 'indicator',
                key: 'indicator',
                width: 120,
                render: (text: string) => <Text style={{ whiteSpace: 'nowrap' }}>{text}</Text>
              },
              {
                title: '数值',
                dataIndex: 'value',
                key: 'value',
                width: 100,
                align: 'right'
              },
              {
                title: '说明',
                dataIndex: 'description',
                key: 'description',
                onCell: () => ({ style: { whiteSpace: 'normal' } }),
                render: (text: string) => (
                  <Text type="secondary" style={{ color: '#8c8c8c', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 12 }}>{text}</Text>
                )
              }
            ]}
            pagination={false}
            size="small"
            loading={renderPending}
            
          />
        )
      },
      {
        key: 'risk',
        label: '风险评估',
        children: (
          <Table
            dataSource={riskData}
            columns={[
              {
                title: '风险指标',
                dataIndex: 'metric',
                key: 'metric',
                width: 120,
                render: (text: string) => <Text style={{ whiteSpace: 'nowrap' }}>{text}</Text>
              },
              {
                title: '数值',
                dataIndex: 'value',
                key: 'value',
                width: 100,
                align: 'right'
              },
              {
                title: '说明',
                dataIndex: 'description',
                key: 'description',
                onCell: () => ({ style: { whiteSpace: 'normal' } }),
                render: (text: string) => (
                  <Tooltip title={text}>
                    <Text type="secondary" style={{ color: '#8c8c8c', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 12 }}>{text}</Text>
                  </Tooltip>
                )
              }
            ]}
            pagination={false}
            size="small"
            loading={renderPending}
            
          />
        )
      }
    ];
  }, [result, strategyColumns, technicalData, riskData, renderPending]);

  // 渲染加载状态
  if (loading) {
    return (
      <div className="analysis-results loading">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>正在分析中...</Text>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    const errorInfo = getErrorInfo(error);
    const getErrorSeverity = (type: string) => {
      switch (type) {
        case 'NETWORK': return { level: 'warning', icon: '🌐' };
        case 'API': return { level: 'error', icon: '⚠️' };
        case 'DATA': return { level: 'warning', icon: '📊' };
        case 'TIMEOUT': return { level: 'warning', icon: '⏱️' };
        default: return { level: 'error', icon: '❌' };
      }
    };

    const severity = getErrorSeverity(errorInfo.type);

    return (
      <div className="analysis-results error">
        <Card 
          title={
            <Space>
              <span style={{ fontSize: '16px' }}>{severity.icon}</span>
              <span>分析失败</span>
              {errorInfo.code && (
                <Tag color="red">错误代码: {errorInfo.code}</Tag>
              )}
            </Space>
          }
          extra={
            <Space>
              <Tag color={errorInfo.retryable ? 'orange' : 'red'}>
                {errorInfo.retryable ? '可重试' : '不可重试'}
              </Tag>
              <Tag color={severity.level === 'error' ? 'red' : 'orange'}>
                {severity.level === 'error' ? '严重' : '警告'}
              </Tag>
            </Space>
          }
        >
          <Alert
            message={
              <div>
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {/* 错误基本信息 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <Text strong>错误类型:</Text>
                          <Tag color="red">{errorInfo.type}</Tag>
                        </Space>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatTime(errorInfo.timestamp)}
                        </Text>
                      </div>

                      {/* 错误消息 */}
                      <div>
                        <Text strong>错误信息:</Text>
                        <div style={{ marginTop: 4, padding: '8px 12px', background: '#fff2f0', borderRadius: '4px', border: '1px solid #ffccc7' }}>
                          <Text>{errorInfo.message}</Text>
                        </div>
                      </div>

                      {/* 详细信息 */}
                      {errorInfo.details && (
                        <div>
                          <Text strong>详细信息:</Text>
                          <div style={{ marginTop: 4, padding: '8px 12px', background: '#fafafa', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                            <Text type="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                              {errorInfo.details}
                            </Text>
                          </div>
                        </div>
                      )}

                      {/* 解决建议 */}
                      {errorInfo.suggestion && (
                        <div>
                          <Text strong>💡 解决建议:</Text>
                          <div style={{ marginTop: 4, padding: '8px 12px', background: '#fffbe6', borderRadius: '4px', border: '1px solid #ffe58f' }}>
                            <Text style={{ color: '#d48806' }}>{errorInfo.suggestion}</Text>
                          </div>
                        </div>
                      )}

                      {/* 操作按钮 */}
                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          {onRefresh && errorInfo.retryable && (
                            <Button 
                              type="primary" 
                              danger 
                              size="small" 
                              icon={<ReloadOutlined />}
                              onClick={onRefresh}
                            >
                              重试分析
                            </Button>
                          )}
                          <Button 
                            size="small" 
                            icon={<InfoCircleOutlined />}
                            onClick={() => setErrorLogVisible(true)}
                          >
                            查看错误日志
                          </Button>
                        </Space>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          错误ID: {errorInfo.timestamp.slice(-8)}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </div>
            }
            type="error"
            showIcon={false}
            style={{ border: 'none', background: 'transparent' }}
          />
        </Card>
      </div>
    );
  }

  // 渲染空状态
  if (!result) {
    return (
      <div className="analysis-results empty">
        <Card>
          <Empty
            description="请选择股票并设置参数进行分析"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      </div>
    );
  }



  return (
    <div className="analysis-results">
      <Card
        title={
          <div className="analysis-header">
            <Space>
              <BarChartOutlined />
              <Title level={4} style={{ margin: 0 }}>
                {(() => {
                  const symbol = (result.symbol || '').trim();
                  const name = (headerName || result.company_name || '').trim();
                  const main = name && name !== symbol ? `${symbol} - ${name}` : `${symbol}`;
                  return (
                    <>
                      <span>{main}</span>
                      <span style={{ fontSize: '14px', fontWeight: 500, marginLeft: 6, color: '#555' }}>分析结果</span>
                    </>
                  );
                })()}
              </Title>
            </Space>
            <Space />
          </div>
        }
        size="small"
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
          size="small"
          tabBarGutter={8}
          moreIcon={null}
          tabBarStyle={{ overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap' }}
        />

        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
          <div style={{ flex: '1 1 0', minWidth: 240, maxWidth: 'calc(100% - 140px)' }}>
            <label htmlFor="paired-stock-input" style={{ display: 'none' }}>统计套利对标股票</label>
            <AutoComplete
              id="paired-stock-input"
              // @ts-ignore
              name="paired_symbol"
              style={{ width: '100%' }}
              placeholder="用于统计套利的对标股票（可选，支持代码/名称）"
              options={pairOptions}
              onSearch={async (text: string) => {
                try {
                  const list = await analysisApi.searchStocks(text || '');
                  let opts = list.map((s: any) => ({ value: s.symbol, label: `${s.symbol} · ${s.name}` }));
                  const norm = normalizeSymbolInput(text);
                  if (opts.length === 0 && /^\d{6}(\.S[HZ])?$/.test(norm)) {
                    opts = [{ value: norm, label: norm }];
                  }
                  setPairOptions(opts);
                } catch {
                  setPairOptions([]);
                }
              }}
              onSelect={(val: string) => {
                const norm = normalizeSymbolInput(val);
                setPairSymbol(norm);
                try {
                  const sym = String(result?.symbol || '').trim();
                  if (sym) {
                    localStorage.setItem(`PAIRED_SYMBOL::${sym}`, norm);
                    // 额外写入无后缀/带后缀版本，确保 ThreeColumnLayout 能读到
                    if (sym.includes('.')) {
                      localStorage.setItem(`PAIRED_SYMBOL::${sym.split('.')[0]}`, norm);
                    } else {
                      localStorage.setItem(`PAIRED_SYMBOL::${sym}.SH`, norm);
                      localStorage.setItem(`PAIRED_SYMBOL::${sym}.SZ`, norm);
                    }
                  }
                } catch { void 0; }
                if (typeof onRefresh === 'function') onRefresh();
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  const inputVal = normalizeSymbolInput(String(e.target.value || ''));
                  setPairSymbol(inputVal);
                  try {
                    const sym = String(result?.symbol || '').trim();
                    if (sym) {
                      localStorage.setItem(`PAIRED_SYMBOL::${sym}`, inputVal);
                      // 额外写入无后缀/带后缀版本
                      if (sym.includes('.')) {
                        localStorage.setItem(`PAIRED_SYMBOL::${sym.split('.')[0]}`, inputVal);
                      } else {
                        localStorage.setItem(`PAIRED_SYMBOL::${sym}.SH`, inputVal);
                        localStorage.setItem(`PAIRED_SYMBOL::${sym}.SZ`, inputVal);
                      }
                    }
                  } catch { void 0; }
                  if (typeof onRefresh === 'function') onRefresh();
                }
              }}
              allowClear
            />
          </div>
          <Button
            type="primary"
            onClick={async () => {
              try {
                const data = result as any;
                let shortName = data.company_name || data.symbol;
                try {
                  const rt = await analysisApi.getRealtimeData(data.symbol);
                  if (rt && rt.name) shortName = rt.name;
                } catch { void 0; }
                // 为PDF图像生成获取完整的价格与技术指标数据
                let stockData = { price_data: [], technical_indicators: [] } as any;
                try {
                  const sd = await analysisApi.getStockData(data.symbol);
                  stockData = sd?.data || stockData;
                } catch { void 0; }
                const exportData = exportService.generateExportData(
                  data.symbol,
                  shortName,
                  {
                    price_data: stockData.price_data || data.price_data || [],
                    technical_indicators: data.technical_indicators || {},
                    performance_data: data.performance_data || [],
                    risk_data: data.risk_metrics ? [{ timestamp: data.timestamp, var: data.risk_metrics.var_95, cvar: data.risk_metrics.cvar_95 } as any] : [],
                    portfolio_data: [],
                    strategies: data.strategies || [],
                    expected_returns_periods: (data as any)?.expected_returns_periods || {},
                    expected_returns_periods_weighted: (data as any)?.expected_returns_periods_weighted || (data as any)?.expected_returns_periods || {},
                    investment_summary: (data as any)?.investment_summary, // 透传投资摘要
                    overview: {
                      overall_signal: (data.overall_signal === 'BUY' ? '买入' : data.overall_signal === 'SELL' ? '卖出' : '观望'),
                      confidence: data.confidence,
                      risk_level: (data.risk_level === 'HIGH' ? '高' : data.risk_level === 'LOW' ? '低' : '中'),
                      expected_return: ((data.expected_returns_periods_weighted || {})[erPeriod])
                        || ((data.expected_returns_periods || {})[erPeriod])
                        || data.expected_return || (data as any)?.expected_return_weighted || 0
                    }
                  }
                );
                (exportData as any).expected_return_days = erPeriod || 20;
                const options: ExportOptions = { format: 'pdf', includeCharts: true, includeTables: true, includeAnalysis: true };
                await exportService.exportReport(exportData, options);
                // 使用 App.useApp().message 代替静态 message
                message.success('报告导出成功');
              } catch (e: any) {
                message.error(`导出失败：${e?.message || '未知错误'}`);
              }
            }}
          >
            导出PDF
          </Button>
        </div>

        {/* 详细时间信息 */}
        <div className="analysis-timestamp">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {/* 主要分析时间 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary">
                    分析完成时间: {formatTime(result.timestamp)}
                  </Text>
                  {result.data_update_time && (
                    <Space>
                      <Text type="secondary">数据新鲜度:</Text>
                      <Badge 
                        color={getDataFreshness(result.data_update_time).color}
                        text={getDataFreshness(result.data_update_time).text}
                      />
                    </Space>
                  )}
                </div>

                {/* 版权说明：显示在时间信息下方靠右位置，年份自动根据当前时间生成 */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {`Copyright © cheesesmart.com ${new Date().getFullYear()} Corporation All Rights Reserved`}
                  </Text>
                </div>

                {/* 版本与构建信息：显示在版权说明下方靠右位置 */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {`版本 ${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.1.0'} · 构建 ${formatDate(String(typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : new Date().toISOString()))}`}
                  </Text>
                </div>

                {/* 详细时间信息 */}
                <Row gutter={[16, 4]}>
                  {result.analysis_start_time && (
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        开始时间: {formatTime(result.analysis_start_time)}
                      </Text>
                    </Col>
                  )}
                  {result.analysis_end_time && (
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        结束时间: {formatTime(result.analysis_end_time)}
                      </Text>
                    </Col>
                  )}
                  {result.analysis_duration && (
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        耗时: {result.analysis_duration < 1000 
                          ? `${result.analysis_duration}ms` 
                          : `${(result.analysis_duration / 1000).toFixed(2)}s`}
                      </Text>
                    </Col>
                  )}
                </Row>

                {/* 数据来源信息 */}
                {(result.data_update_time || result.data_source) && (
                  <Row gutter={[16, 4]}>
                    {result.data_update_time && (
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          数据更新: {formatTime(result.data_update_time)}
                        </Text>
                      </Col>
                    )}
                    {result.data_source && (
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          数据来源: {result.data_source}
                        </Text>
                      </Col>
                    )}
                  </Row>
                )}
              </Space>
            </Col>
          </Row>
        </div>
      </Card>

      {/* 错误日志查看器 */}
      <ErrorLogViewer
        visible={errorLogVisible}
        onClose={() => setErrorLogVisible(false)}
      />

      {/* 风险详情弹窗：国际标准描述（置于组件内） */}
      <Modal
        title="风险详情（国际标准）"
        open={riskModalVisible}
        onOk={() => setRiskModalVisible(false)}
        onCancel={() => setRiskModalVisible(false)}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Statistic title="VaR (95%)" value={Math.abs((result?.risk_metrics?.var_95 ?? 0))} precision={4} />
          <Statistic title="CVaR (95%)" value={Math.abs((result?.risk_metrics?.cvar_95 ?? 0))} precision={4} />
          <Statistic title="年化波动率" value={Math.abs((result?.risk_metrics?.volatility ?? 0))} precision={4} />
          <Paragraph type="secondary">
            说明：VaR/CVaR为在95%置信水平下的潜在损失与条件损失；波动率为基于日收益率换算的年化标准差。指标越高，风险越大。
          </Paragraph>
        </Space>
      </Modal>

      {/* 投资决策摘要弹窗 */}
      <Modal
        title="投资决策摘要"
        open={investmentSummaryVisible}
        onCancel={() => setInvestmentSummaryVisible(false)}
        footer={null}
        width={700}
      >
        {result?.investment_summary ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 顶部核心信号 */}
            <div style={{ 
              background: result.overall_signal === 'BUY' ? '#f6ffed' : 
                          result.overall_signal === 'SELL' ? '#fff1f0' : '#e6f7ff',
              border: `1px solid ${
                result.overall_signal === 'BUY' ? '#b7eb8f' : 
                result.overall_signal === 'SELL' ? '#ffa39e' : '#91d5ff'
              }`,
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <Title level={4} style={{ margin: 0, color: getSignalColor(result.overall_signal) }}>
                  {result.investment_summary.final_signal.display}
                </Title>
                <Text type="secondary">基于多策略综合评分与风险调整</Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {Number(result.investment_summary.scores.comprehensive * 100).toFixed(1)}
                </div>
                <Space size={4}>
                  <Text type="secondary">综合评分</Text>
                  <Tooltip title={
                    <div>
                      <p>综合评分基于多策略信号、置信度及预期收益率加权计算。</p>
                      <p>• 基准分通常在0-100之间</p>
                      <p>• 若超过100分，表示预期收益率贡献了显著的超额加分</p>
                      <p>• 分数越高代表买入信号越强且置信度越高</p>
                    </div>
                  }>
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                  </Tooltip>
                </Space>
              </div>
            </div>

            {/* 核心矛盾与风险评估 */}
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="核心矛盾解读">
                  <Text strong>{result.investment_summary.core_contradiction}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">分歧度惩罚因子: </Text>
                    <Text>{(result.investment_summary.divergence_analysis.penalty_factor * 100).toFixed(1)}%</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="风险收益评估">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text>风险水平:</Text>
                    <Tag color={getRiskColor(result.risk_level)}>{result.investment_summary.risk_assessment.description}</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <Text>调整后置信度:</Text>
                    <Progress 
                      percent={Math.round(result.investment_summary.scores.confidence_adjusted * 100)} 
                      size="small" 
                      steps={5}
                      strokeColor={result.investment_summary.scores.confidence_adjusted > 0.6 ? '#52c41a' : '#faad14'}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* 操作建议 */}
            <Card size="small" title="操作建议" style={{ borderLeft: '4px solid #1890ff' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <Text strong style={{ marginRight: 8 }}>【持仓者】:</Text>
                  <Text>{result.investment_summary.action_advice.holder}</Text>
                </div>
                <div>
                  <Text strong style={{ marginRight: 8 }}>【持币者】:</Text>
                  <Text>{result.investment_summary.action_advice.buyer}</Text>
                </div>
              </div>
            </Card>

            {/* 关键数据 */}
            <div style={{ background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic 
                    title="预期5日收益" 
                    value={result.investment_summary.key_metrics.expected_return_5d} 
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="近期波动率" 
                    value={result.risk_metrics.volatility ? `${(result.risk_metrics.volatility * 100).toFixed(2)}%` : 'N/A'} 
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
              </Row>
            </div>

            {/* 算法参与标识 */}
            {result.investment_summary.algorithm_participation && (
              <div style={{ marginTop: 0, padding: '12px', background: '#f0f5ff', borderRadius: '4px', border: '1px solid #adc6ff' }}>
                <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    <Text strong>算法参与计算</Text>
                  </Space>
                  <Space>
                    <Tag color={result.investment_summary.algorithm_participation.is_active ? "green" : "red"}>
                      {result.investment_summary.algorithm_participation.is_active ? "是" : "否"}
                    </Tag>
                    {result.investment_summary.algorithm_participation.is_active && (
                      <Text type="secondary">
                        ({result.investment_summary.algorithm_participation.active_count}个策略参与，总权重 {(result.investment_summary.algorithm_participation.total_weight * 100).toFixed(0)}%)
                      </Text>
                    )}
                  </Space>
                </Space>
              </div>
            )}

            {/* 高级风险模型展示 */}
            {result.investment_summary.advanced_risk_model && result.investment_summary.advanced_risk_model.risk_penalty < 1.0 && (
              <div style={{ marginTop: 12, padding: '12px', background: '#fff2f0', borderRadius: '4px', border: '1px solid #ffccc7' }}>
                 <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <WarningOutlined style={{ color: '#ff4d4f' }} />
                      <Text strong style={{ color: '#cf1322' }}>高级风险模型预警</Text>
                      <Tag color="red">
                        系统性惩罚: {((1 - result.investment_summary.advanced_risk_model.risk_penalty) * 100).toFixed(0)}%
                      </Tag>
                    </Space>
                    <div style={{ paddingLeft: 24 }}>
                      <Text type="secondary">检测到潜在宏观/特质风险，系统已自动降低仓位：</Text>
                      <ul style={{ margin: '4px 0', paddingLeft: 20, color: '#cf1322' }}>
                        {result.investment_summary.advanced_risk_model.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                 </Space>
              </div>
            )}
          </div>
        ) : (
          <Empty description="暂无详细摘要数据，请重新分析" />
        )}
      </Modal>

      {/* 设置弹窗（已移至图表数据区域），此处不再提供重复入口 */}
    </div>
  );
};

export default AnalysisResults;
  const renderPending = false;
